import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  TrendingUp, Users, ShieldCheck, Siren, Mail, Plus, Trash2, Pause, Play,
  FileText, FileSpreadsheet, Clock, BarChart3, PieChart, Loader2,
  ChevronRight, ClipboardCheck, Send, Eye,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart as RePieChart, Pie, Cell,
} from 'recharts';
import api, { formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { IncidentDialog } from '../components/dialogs/IncidentDialog';
import { DispatchReportPanel } from '../components/reports/DispatchReportPanel';
import { BillingHealthPanel } from '../components/reports/BillingHealthPanel';
import { DateRangePresets, fmtLocal } from '../components/reports/DateRangePresets';
import { useLiveEvents } from '../lib/events';

const fmtR = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtInt = (v) => Number(v || 0).toLocaleString('en-ZA');
const today = () => fmtLocal(new Date());
const monthAgo = () => { const d = new Date(); d.setDate(d.getDate() - 30); return fmtLocal(d); };

const REPORT_CODES = [
  { code: 'overview', label: 'Operational + Financial Overview' },
  { code: 'incidents_by_service', label: 'Incidents by Service Type' },
  { code: 'monthly_trend', label: 'Monthly Trend (6 months)' },
  { code: 'revenue_by_client', label: 'Revenue by Client' },
  { code: 'contractor_performance', label: 'Contractor Performance' },
];

const PIE_COLORS = ['#0D5B69', '#80C030', '#0EA5A5', '#F59E0B', '#EF4444', '#8B5CF6', '#64748B'];


export const Reports = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [start, setStart] = useState(monthAgo());
  const [end, setEnd] = useState(today());

  return (
    <Layout title="Reports" subtitle="Operational & financial analytics, scheduled delivery">
      <div className="space-y-6 animate-fade-in" data-testid="reports-page">
        <DateRangeBar start={start} end={end} setStart={setStart} setEnd={setEnd} />

        <Tabs defaultValue={isAdmin ? 'drafts' : 'operational'} className="w-full">
          <TabsList className="bg-slate-100">
            {isAdmin && (
              <TabsTrigger value="drafts" className="gap-2" data-testid="tab-drafts">
                <ClipboardCheck className="w-4 h-4" /> Draft Reports
              </TabsTrigger>
            )}
            <TabsTrigger value="all-incidents" className="gap-2" data-testid="tab-all-incidents">
              <FileText className="w-4 h-4" /> All Incidents
            </TabsTrigger>
            <TabsTrigger value="operational" className="gap-2" data-testid="tab-operational">
              <Siren className="w-4 h-4" /> Operational
            </TabsTrigger>
            <TabsTrigger value="financial" className="gap-2" data-testid="tab-financial">
              <TrendingUp className="w-4 h-4" /> Financial
            </TabsTrigger>
            <TabsTrigger value="contractors" className="gap-2" data-testid="tab-contractors">
              <ShieldCheck className="w-4 h-4" /> Contractors
            </TabsTrigger>
            <TabsTrigger value="customers" className="gap-2" data-testid="tab-customers">
              <Users className="w-4 h-4" /> Customers
            </TabsTrigger>
            <TabsTrigger value="dispatch" className="gap-2" data-testid="tab-dispatch">
              <FileText className="w-4 h-4" /> Dispatch
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="billing-health" className="gap-2" data-testid="tab-billing-health">
                <ShieldCheck className="w-4 h-4" /> Billing Health
              </TabsTrigger>
            )}
            <TabsTrigger value="scheduled" className="gap-2" data-testid="tab-scheduled">
              <Clock className="w-4 h-4" /> Scheduled
            </TabsTrigger>
          </TabsList>

          {isAdmin && (
            <TabsContent value="drafts" className="mt-4">
              <DraftReportsPanel />
            </TabsContent>
          )}
          <TabsContent value="all-incidents" className="mt-4">
            <AllIncidentsPanel start={start} end={end} />
          </TabsContent>
          <TabsContent value="operational" className="mt-4">
            <OperationalPanel start={start} end={end} />
          </TabsContent>
          <TabsContent value="financial" className="mt-4">
            <FinancialPanel start={start} end={end} />
          </TabsContent>
          <TabsContent value="contractors" className="mt-4">
            <ContractorsPanel start={start} end={end} />
          </TabsContent>
          <TabsContent value="customers" className="mt-4">
            <CustomersPanel start={start} end={end} />
          </TabsContent>
          <TabsContent value="dispatch" className="mt-4">
            <DispatchReportPanel start={start} end={end} />
          </TabsContent>
          {isAdmin && (
            <TabsContent value="billing-health" className="mt-4">
              <BillingHealthPanel start={start} end={end} />
            </TabsContent>
          )}
          <TabsContent value="scheduled" className="mt-4">
            <ScheduledPanel />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};


const DateRangeBar = ({ start, end, setStart, setEnd }) => (
  <Card className="bg-white border-slate-200">
    <CardContent className="p-4 space-y-3">
      <DateRangePresets
        start={start} end={end} setStart={setStart} setEnd={setEnd}
        testidPrefix="reports-preset"
      />
      <div className="flex flex-col md:flex-row md:items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs uppercase tracking-wide text-slate-600">From</Label>
          <Input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)} data-testid="reports-start-date" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs uppercase tracking-wide text-slate-600">To</Label>
          <Input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} data-testid="reports-end-date" />
        </div>
        <p className="text-xs text-slate-500 ml-auto self-center">
          Pick a period above or set your own dates — every KPI, chart, table and export below
          uses it, and the last day is always included in full.
        </p>
      </div>
    </CardContent>
  </Card>
);


const StatCard = ({ label, value, sub, accent }) => (
  <Card className={`bg-white border-slate-200 ${accent ? 'border-[#80C030]/40' : ''}`}>
    <CardContent className="p-4">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-2xl font-bold font-heading mt-1 ${accent ? 'text-[#80C030]' : 'text-[#0D5B69]'}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-1">{sub}</p>}
    </CardContent>
  </Card>
);


// ---------------- OPERATIONAL ----------------

const OperationalPanel = ({ start, end }) => {
  const [kpi, setKpi] = useState(null);
  const [byService, setByService] = useState([]);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get('/reports/overview', { params: { start, end } }),
      api.get('/reports/incidents-by-service', { params: { start, end } }),
      api.get('/reports/monthly-trend', { params: { months: 6 } }),
    ]).then(([a, b, c]) => {
      if (!alive) return;
      setKpi(a.data); setByService(b.data); setTrend(c.data);
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [start, end]);

  if (loading) return <PanelLoading />;
  if (!kpi) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="operational-kpis">
        <StatCard label="Total Incidents" value={fmtInt(kpi.total_incidents)} sub={`${kpi.active_now} active now`} />
        <StatCard label="Avg Response" value={`${kpi.avg_response_minutes} min`} sub="First contractor on scene" />
        <StatCard label="Resolution Rate" value={`${kpi.resolution_rate_pct}%`} sub={`${(kpi.by_status?.closed || 0) + (kpi.by_status?.cancelled || 0)} of ${kpi.total_incidents} resolved`} accent />
        <StatCard label="Active Now" value={fmtInt(kpi.active_now)} sub="Open + in-progress" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border-slate-200" data-testid="incidents-by-service-chart">
          <ChartHeader icon={<BarChart3 className="w-5 h-5 text-[#0D5B69]" />} title="Incidents by Service Type" reportCode="incidents_by_service" start={start} end={end} />
          <CardContent className="p-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byService} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis dataKey="service_name" type="category" tick={{ fontSize: 11, fill: '#64748b' }} width={120} />
                <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12 }} />
                <Bar dataKey="incidents" fill="#0D5B69" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200" data-testid="monthly-trend-chart">
          <ChartHeader icon={<TrendingUp className="w-5 h-5 text-[#80C030]" />} title="Monthly Incident Trend (6 months)" reportCode="monthly_trend" start={start} end={end} />
          <CardContent className="p-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12 }} />
                <Line type="monotone" dataKey="incidents" stroke="#80C030" strokeWidth={2} dot={{ fill: '#80C030' }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};


// ---------------- FINANCIAL ----------------

const FinancialPanel = ({ start, end }) => {
  const [kpi, setKpi] = useState(null);
  const [byClient, setByClient] = useState([]);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get('/reports/overview', { params: { start, end } }),
      api.get('/reports/revenue-by-client', { params: { start, end } }),
      api.get('/reports/monthly-trend', { params: { months: 6 } }),
    ]).then(([a, b, c]) => {
      if (!alive) return;
      setKpi(a.data); setByClient(b.data); setTrend(c.data);
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [start, end]);

  if (loading) return <PanelLoading />;
  if (!kpi) return null;

  const pieData = byClient.slice(0, 6).map((c, i) => ({ name: c.name, value: c.revenue, color: PIE_COLORS[i % PIE_COLORS.length] }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="financial-kpis">
        <StatCard label="Revenue (incl VAT)" value={fmtR(kpi.revenue_total_incl_vat)} accent />
        <StatCard label="Subtotal (ex VAT)" value={fmtR(kpi.revenue_subtotal)} />
        <StatCard label="Contractor Cost" value={fmtR(kpi.contractor_cost)} />
        <StatCard label="Gross Profit" value={fmtR(kpi.gross_profit)} sub="Subtotal − Contractor Cost" accent={kpi.gross_profit >= 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border-slate-200" data-testid="revenue-by-client-chart">
          <ChartHeader icon={<PieChart className="w-5 h-5 text-[#0D5B69]" />} title="Revenue by Client" reportCode="revenue_by_client" start={start} end={end} />
          <CardContent className="p-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <RePieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={90} dataKey="value"
                     label={({ name, percent }) => percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : ''}
                     labelLine={false}>
                  {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtR(v)} contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12 }} />
              </RePieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200" data-testid="revenue-trend-chart">
          <ChartHeader icon={<TrendingUp className="w-5 h-5 text-[#80C030]" />} title="Revenue Trend" reportCode="monthly_trend" start={start} end={end} />
          <CardContent className="p-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `R${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmtR(v)} contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12 }} />
                <Bar dataKey="revenue" fill="#0D5B69" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <ProfitabilityPanel start={start} end={end} />
    </div>
  );
};


// ---------------- CONTRACTORS ----------------

const ContractorsPanel = ({ start, end }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // { kind, id, name, start, end }

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/reports/contractor-performance', { params: { start, end } })
      .then((r) => alive && setRows(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [start, end]);

  if (loading) return <PanelLoading />;

  return (
    <>
    <Card className="bg-white border-slate-200" data-testid="contractor-performance-card">
      <ChartHeader icon={<ShieldCheck className="w-5 h-5 text-[#0D5B69]" />}
                   title="Contractor Performance" reportCode="contractor_performance" start={start} end={end} />
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                {['Contractor', 'Incidents', 'Hours', 'Revenue', 'Cost', 'Margin', ''].map((h) => (
                  <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider p-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7} className="text-center p-10 text-slate-400">No contractor activity in this period.</td></tr>
              ) : rows.map((r, idx) => (
                <tr
                  key={r.contractor_id}
                  onClick={() => setDrill({ kind: 'contractor', id: r.contractor_id, name: r.name, start, end })}
                  className="border-b border-slate-100 hover:bg-[#80C030]/5 cursor-pointer transition-colors"
                  data-testid={`contractor-row-${idx}`}
                  title="Click to drill down — see every incident"
                >
                  <td className="p-4 font-medium text-sm text-slate-900">{r.name}</td>
                  <td className="p-4 text-sm text-slate-700">{fmtInt(r.incidents)}</td>
                  <td className="p-4 text-sm text-slate-700">{r.hours}h</td>
                  <td className="p-4 font-mono text-sm font-semibold text-slate-900">{fmtR(r.revenue)}</td>
                  <td className="p-4 font-mono text-sm text-slate-600">{fmtR(r.cost)}</td>
                  <td className={`p-4 font-mono text-sm font-semibold ${r.margin >= 0 ? 'text-[#80C030]' : 'text-red-500'}`}>{fmtR(r.margin)}</td>
                  <td className="p-4 text-right"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
    <DrillDownDialog drill={drill} onClose={() => setDrill(null)} />
    </>
  );
};


// ---------------- CUSTOMERS ----------------

const CustomersPanel = ({ start, end }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/reports/revenue-by-client', { params: { start, end } })
      .then((r) => alive && setRows(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [start, end]);

  const totalRev = useMemo(() => rows.reduce((a, r) => a + r.revenue, 0), [rows]);
  if (loading) return <PanelLoading />;

  return (
    <>
    <Card className="bg-white border-slate-200" data-testid="customer-revenue-card">
      <ChartHeader icon={<Users className="w-5 h-5 text-[#0D5B69]" />}
                   title="Revenue by Customer" reportCode="revenue_by_client" start={start} end={end} />
      <CardContent className="p-0">
        <p className="text-[11px] text-slate-400 px-4 pt-3 italic">Click any row to drill down — every incident, its segments, rates and totals.</p>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                {['Customer', 'Incidents', 'Revenue', '% of Total', ''].map((h) => (
                  <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider p-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={5} className="text-center p-10 text-slate-400">No customer revenue in this period.</td></tr>
              ) : rows.map((r, i) => {
                const pct = totalRev > 0 ? (r.revenue / totalRev) * 100 : 0;
                return (
                  <tr
                    key={r.customer_id}
                    onClick={() => setDrill({ kind: 'customer', id: r.customer_id, name: r.name, start, end })}
                    className="border-b border-slate-100 hover:bg-[#80C030]/5 cursor-pointer transition-colors"
                    data-testid={`customer-row-${i}`}
                    title="Click to drill down — see every incident"
                  >
                    <td className="p-4 font-medium text-sm text-slate-900">{r.name}</td>
                    <td className="p-4 text-sm text-slate-700">{fmtInt(r.incidents)}</td>
                    <td className="p-4 font-mono text-sm font-semibold text-slate-900">{fmtR(r.revenue)}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                        </div>
                        <span className="text-xs text-slate-600">{pct.toFixed(0)}%</span>
                      </div>
                    </td>
                    <td className="p-4 text-right"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
    <DrillDownDialog drill={drill} onClose={() => setDrill(null)} />
    </>
  );
};


// ---------------- SCHEDULED ----------------

const ScheduledPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/scheduled-reports')
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (it) => {
    setBusyId(it.id);
    try {
      await api.patch(`/scheduled-reports/${it.id}`, { active: !it.active });
      toast.success(`Schedule ${it.active ? 'paused' : 'resumed'}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(''); }
  };

  const runNow = async (it) => {
    setBusyId(it.id);
    try {
      const r = await api.post(`/scheduled-reports/${it.id}/run-now`);
      toast.success(`Report sent to ${r.data.recipients.join(', ') || 'no recipients'}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(''); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete schedule "${it.name}"?`)) return;
    setBusyId(it.id);
    try {
      await api.delete(`/scheduled-reports/${it.id}`);
      toast.success('Schedule deleted');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(''); }
  };

  const activeCount = items.filter((i) => i.active).length;

  return (
    <div className="space-y-4" data-testid="scheduled-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Active Schedules" value={fmtInt(activeCount)} sub={`${items.length - activeCount} paused`} accent />
        <StatCard label="Total Schedules" value={fmtInt(items.length)} />
        <StatCard label="Tick Interval" value="1 min" sub="Backend scheduler" />
        <StatCard label="Last Run" value={items.find((i) => i.last_run_at)?.last_run_at?.slice(0, 16).replace('T', ' ') || '—'} />
      </div>

      <Card className="bg-white border-slate-200" data-testid="scheduled-reports-card">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading text-lg text-[#0D5B69] flex items-center gap-2">
              <Mail className="w-5 h-5" /> Scheduled Reports
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Each schedule auto-runs at the configured time, builds the chosen format, and emails recipients via Resend.
            </p>
          </div>
          <Button className="bg-[#80C030] hover:bg-[#6FA528] text-white gap-1 font-semibold" onClick={() => setOpen(true)} data-testid="new-schedule-btn">
            <Plus className="w-4 h-4" /> Schedule New Report
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? <PanelLoading /> : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    {['Schedule', 'Frequency', 'Format', 'Recipients', 'Next Run', 'Status', 'Actions'].map((h) => (
                      <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider p-4">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr><td colSpan={7} className="text-center p-10 text-slate-400">No scheduled reports yet. Click "Schedule New Report" to create one.</td></tr>
                  ) : items.map((it, idx) => (
                    <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50/80" data-testid={`scheduled-report-row-${idx}`}>
                      <td className="p-4">
                        <p className="font-medium text-sm text-slate-900">{it.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {REPORT_CODES.find((r) => r.code === it.report_code)?.label || it.report_code}
                        </p>
                      </td>
                      <td className="p-4 text-sm text-slate-700 capitalize">
                        {it.frequency}
                        <p className="text-xs text-slate-500">{it.run_time} · day {it.run_day}</p>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline" className="uppercase text-[10px]">{it.format}</Badge>
                      </td>
                      <td className="p-4 text-xs text-slate-600">
                        {(it.recipients || []).slice(0, 2).map((r) => <p key={r}>{r}</p>)}
                        {(it.recipients || []).length > 2 && <p className="text-slate-400">+{it.recipients.length - 2} more</p>}
                      </td>
                      <td className="p-4 text-xs text-slate-700">
                        {it.next_run_at?.slice(0, 16).replace('T', ' ') || '—'}
                        <p className="text-[11px] text-slate-400 mt-0.5">last: {it.last_run_at?.slice(0, 16).replace('T', ' ') || 'never'}</p>
                      </td>
                      <td className="p-4">
                        {it.active
                          ? <Badge className="bg-[#80C030]/10 text-[#5a8c20] border-[#80C030]/30">Active</Badge>
                          : <Badge variant="outline" className="text-slate-500">Paused</Badge>}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => toggle(it)}
                                  disabled={busyId === it.id} data-testid={`schedule-toggle-${idx}`}
                                  title={it.active ? 'Pause' : 'Resume'}>
                            {it.active ? <Pause className="w-4 h-4 text-slate-500" /> : <Play className="w-4 h-4 text-[#80C030]" />}
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => runNow(it)}
                                  disabled={busyId === it.id} data-testid={`schedule-run-now-${idx}`} title="Run now">
                            {busyId === it.id ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" /> : <Mail className="w-4 h-4 text-blue-600" />}
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => remove(it)}
                                  disabled={busyId === it.id} data-testid={`schedule-delete-${idx}`} title="Delete">
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <NewScheduleDialog open={open} setOpen={setOpen} onSaved={load} />
    </div>
  );
};


const NewScheduleDialog = ({ open, setOpen, onSaved }) => {
  const [form, setForm] = useState({
    name: '', report_code: 'revenue_by_client', frequency: 'monthly',
    run_day: 1, run_time: '06:00', format: 'pdf',
    period_filter: 'previous_month', recipients: '', notes: '', active: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({
      name: '', report_code: 'revenue_by_client', frequency: 'monthly',
      run_day: 1, run_time: '06:00', format: 'pdf',
      period_filter: 'previous_month', recipients: '', notes: '', active: true,
    });
  }, [open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Schedule name is required'); return; }
    const recipients = form.recipients.split(/[,\s]+/).filter(Boolean);
    if (!recipients.length) { toast.error('At least one recipient email is required'); return; }
    setSaving(true);
    try {
      await api.post('/scheduled-reports', {
        name: form.name.trim(),
        report_code: form.report_code,
        frequency: form.frequency,
        run_day: Number(form.run_day),
        run_time: form.run_time,
        format: form.format,
        period_filter: form.period_filter,
        recipients,
        notes: form.notes || null,
        active: form.active,
      });
      toast.success('Schedule saved');
      setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#0D5B69]">Schedule New Report</DialogTitle>
          <DialogDescription>The CSG scheduler runs every minute and emails this report on the configured cadence.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2 space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">Schedule Name</Label>
            <Input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="e.g. Monthly Revenue by Client" data-testid="schedule-name-input" />
          </div>

          <div className="col-span-2 space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">Report</Label>
            <Select value={form.report_code} onValueChange={(v) => update('report_code', v)}>
              <SelectTrigger data-testid="schedule-report-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REPORT_CODES.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">Frequency</Label>
            <Select value={form.frequency} onValueChange={(v) => update('frequency', v)}>
              <SelectTrigger data-testid="schedule-frequency-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">
              Day {form.frequency === 'weekly' ? '(1=Mon..7=Sun)' : form.frequency === 'monthly' || form.frequency === 'quarterly' ? '(1–28)' : ''}
            </Label>
            <Input type="number" min={1} max={form.frequency === 'weekly' ? 7 : 28} value={form.run_day}
                   onChange={(e) => update('run_day', e.target.value)} data-testid="schedule-day-input" />
          </div>

          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">Run Time (UTC)</Label>
            <Input type="time" value={form.run_time} onChange={(e) => update('run_time', e.target.value)} data-testid="schedule-time-input" />
          </div>

          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">Format</Label>
            <Select value={form.format} onValueChange={(v) => update('format', v)}>
              <SelectTrigger data-testid="schedule-format-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pdf">PDF</SelectItem>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="xlsx">Excel (XLSX)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2 space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">Period Filter</Label>
            <Select value={form.period_filter} onValueChange={(v) => update('period_filter', v)}>
              <SelectTrigger data-testid="schedule-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="previous_day">Previous Day</SelectItem>
                <SelectItem value="previous_week">Previous Week</SelectItem>
                <SelectItem value="previous_month">Previous Month</SelectItem>
                <SelectItem value="month_to_date">Month to Date</SelectItem>
                <SelectItem value="ytd">Year to Date</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2 space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">Recipients</Label>
            <Textarea rows={2} value={form.recipients} onChange={(e) => update('recipients', e.target.value)}
                      placeholder="accounts@csgconnect.co.za, archive@csgconnect.co.za" data-testid="schedule-recipients-input" />
            <p className="text-[11px] text-slate-400">Comma-separated emails.</p>
          </div>

          <div className="col-span-2 space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">Notes (optional)</Label>
            <Input value={form.notes} onChange={(e) => update('notes', e.target.value)} placeholder="Goes in the email body" data-testid="schedule-notes-input" />
          </div>

          <div className="col-span-2 flex items-center gap-3 p-3 bg-slate-50 rounded-md">
            <Switch checked={form.active} onCheckedChange={(v) => update('active', v)} data-testid="schedule-active-switch" />
            <div className="flex-1">
              <Label className="text-sm font-medium text-slate-900">Active immediately</Label>
              <p className="text-xs text-slate-500">Disable to save the schedule paused.</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="schedule-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#80C030] hover:bg-[#6FA528] text-white font-semibold gap-1" data-testid="schedule-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Save Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ---------------- Shared bits ----------------

const ChartHeader = ({ icon, title, reportCode, start, end }) => {
  const exportAs = async (fmt) => {
    try {
      const r = await api.get(`/reports/${reportCode}/export`, { params: { fmt, start, end }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${reportCode}.${fmt}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };
  return (
    <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <CardTitle className="font-heading text-lg text-[#0D5B69]">{title}</CardTitle>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs" onClick={() => exportAs('csv')} data-testid={`export-${reportCode}-csv`}>
            <FileSpreadsheet className="w-3.5 h-3.5" /> CSV
          </Button>
          <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs" onClick={() => exportAs('xlsx')} data-testid={`export-${reportCode}-xlsx`}>
            <FileSpreadsheet className="w-3.5 h-3.5" /> XLSX
          </Button>
          <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs" onClick={() => exportAs('pdf')} data-testid={`export-${reportCode}-pdf`}>
            <FileText className="w-3.5 h-3.5" /> PDF
          </Button>
        </div>
      </div>
    </CardHeader>
  );
};


// ---------------- DRAFT REPORTS (admin only) ----------------

const DraftReportsPanel = () => {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending'); // pending | sent | all
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [openIncident, setOpenIncident] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [sendDialog, setSendDialog] = useState(null); // {item, recipients, note}
  const [sendBusy, setSendBusy] = useState(false);
  const refreshTimer = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/incidents/reports-queue', { params: { queue_status: statusFilter, days } });
      setItems(r.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [statusFilter, days]);

  useEffect(() => { load(); }, [load]);

  // Lookups for the IncidentDialog (so the admin can edit notes/captions/outcome inline)
  useEffect(() => {
    Promise.all([api.get('/customers'), api.get('/services'), api.get('/contractors')])
      .then(([c, s, co]) => { setCustomers(c.data || []); setServices(s.data || []); setContractors(co.data || []); })
      .catch(() => { /* tolerated */ });
  }, []);

  // Live refresh — incident closed / cancelled / reopened changes the queue.
  // Debounce reloads so a burst of events doesn't slam the API.
  useLiveEvents(
    ['incident.closed', 'incident.cancelled', 'incident.reopened',
      'incident.updated', 'incident.note_added', 'incident.note_updated',
      'incident.note_deleted', 'incident.attachment_added', 'incident.attachment_updated'],
    () => {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(() => load(), 800);
    },
  );

  const openPreview = async (item) => {
    // Fetch the full incident document so IncidentDialog has notes/attachments etc.
    try {
      const r = await api.get(`/incidents/${item.id}`);
      setOpenIncident(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadPdf = async (item) => {
    try {
      const r = await api.get(`/incidents/${item.id}/report.pdf`, {
        responseType: 'blob',
        params: { t: Date.now() },
      });
      const u = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = u; a.download = `${item.incident_no}-report.pdf`; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error('Download failed'); }
  };

  const openSendDialog = (item) => {
    setSendDialog({
      item,
      recipients: item.customer_email || '',
      note: '',
    });
  };

  const handleSend = async () => {
    const recipients = sendDialog.recipients.split(/[,;\s]+/).filter(Boolean);
    if (!recipients.length) { toast.error('Add at least one recipient'); return; }
    setSendBusy(true);
    try {
      const r = await api.post(`/incidents/${sendDialog.item.id}/report/send`, {
        recipients, note: sendDialog.note || null,
      });
      toast.success(`Report sent to ${r.data.recipients.join(', ')}`);
      setSendDialog(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSendBusy(false); }
  };

  const counts = useMemo(() => ({
    total: items.length,
    notes: items.reduce((a, i) => a + (i.notes_count || 0), 0),
    attachments: items.reduce((a, i) => a + (i.attachments_count || 0), 0),
  }), [items]);

  return (
    <div className="space-y-4" data-testid="draft-reports-panel">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniKpi label={statusFilter === 'sent' ? 'Sent' : statusFilter === 'all' ? 'Total' : 'Pending Review'} value={fmtInt(counts.total)} accent={statusFilter === 'pending'} />
        <MiniKpi label="Operator Notes" value={fmtInt(counts.notes)} />
        <MiniKpi label="Photos / Attachments" value={fmtInt(counts.attachments)} />
        <MiniKpi label="Window" value={`${days} days`} />
      </div>

      <Card className="bg-white border-slate-200" data-testid="drafts-card">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <CardTitle className="font-heading text-lg text-[#0D5B69] flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5" /> Draft Reports — Admin Review
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Recently closed / cancelled incidents that need admin sign-off before the customer-facing report goes out.
                Click <b>Preview &amp; Edit</b> to refine notes, captions and outcome — then <b>Send to Client</b>.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36" data-testid="drafts-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending Review</SelectItem>
                  <SelectItem value="sent">Already Sent</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
              <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                <SelectTrigger className="w-28" data-testid="drafts-window"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? <PanelLoading /> : items.length === 0 ? (
            <p className="text-center text-slate-400 py-10">
              {statusFilter === 'pending'
                ? 'No incidents pending review — all caught up.'
                : 'No incidents in this filter.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    {['Incident #', 'Customer', 'Service', 'Status', 'Closed/Cancelled', 'Notes / Photos', 'Report Status', 'Actions'].map((h) => (
                      <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider p-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50/80" data-testid={`draft-row-${idx}`}>
                      <td className="p-3 font-mono text-xs font-semibold text-[#0D5B69]">{it.incident_no}</td>
                      <td className="p-3">
                        <p className="text-sm font-medium text-slate-900">{it.customer_name}</p>
                        {it.customer_email && <p className="text-[11px] text-slate-500">{it.customer_email}</p>}
                      </td>
                      <td className="p-3 text-sm text-slate-700">{it.service_name}</td>
                      <td className="p-3">
                        <Badge className={it.status === 'closed'
                          ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                          : 'bg-slate-100 text-slate-600 border-slate-200'}>
                          {it.status === 'closed' ? 'Closed' : 'Cancelled'}
                        </Badge>
                      </td>
                      <td className="p-3 text-xs text-slate-600">
                        {(it.last_change_at || '').slice(0, 16).replace('T', ' ')}
                      </td>
                      <td className="p-3 text-xs text-slate-600">
                        {it.notes_count} note{it.notes_count === 1 ? '' : 's'} ·{' '}
                        {it.attachments_count} photo{it.attachments_count === 1 ? '' : 's'}
                      </td>
                      <td className="p-3">
                        {it.report_sent_at ? (
                          <div>
                            <Badge className="bg-[#80C030]/10 text-[#5a8c20] border-[#80C030]/30">Sent</Badge>
                            <p className="text-[10px] text-slate-500 mt-0.5">
                              {(it.report_sent_at || '').slice(0, 16).replace('T', ' ')}
                            </p>
                            <p className="text-[10px] text-slate-400 truncate max-w-[160px]">
                              → {(it.report_sent_to || []).join(', ')}
                            </p>
                          </div>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200">Pending review</Badge>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => openPreview(it)} data-testid={`preview-draft-${idx}`} title="Open & edit incident">
                            <Eye className="w-3.5 h-3.5" /> Preview & Edit
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => downloadPdf(it)} data-testid={`download-draft-${idx}`} title="Download PDF preview">
                            <FileText className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => openSendDialog(it)}
                            className="h-8 gap-1 text-xs bg-[#80C030] hover:bg-[#6FA528] text-white font-semibold"
                            data-testid={`send-draft-${idx}`}
                            title="Send report to client"
                          >
                            <Send className="w-3.5 h-3.5" /> {it.report_sent_at ? 'Resend' : 'Send to Client'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Admin opens the incident in the existing dialog — full edit access to notes, captions, outcome */}
      {openIncident && (
        <IncidentDialog
          open
          onOpenChange={(o) => !o && setOpenIncident(null)}
          incident={openIncident}
          customers={customers}
          services={services}
          contractors={contractors}
          onSaved={async (updated) => {
            setOpenIncident(updated);
            load();
          }}
        />
      )}

      {/* Send confirmation */}
      <Dialog open={!!sendDialog} onOpenChange={(o) => !o && setSendDialog(null)}>
        <DialogContent className="max-w-md" data-testid="send-draft-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0D5B69] flex items-center gap-2"><Send className="w-5 h-5" /> Send Incident Report</DialogTitle>
            <DialogDescription>
              Marks <b>{sendDialog?.item?.incident_no}</b> as reviewed and emails the branded PDF to the customer.
            </DialogDescription>
          </DialogHeader>
          {sendDialog && (
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Recipients</Label>
                <Textarea
                  rows={2}
                  value={sendDialog.recipients}
                  onChange={(e) => setSendDialog((s) => ({ ...s, recipients: e.target.value }))}
                  placeholder="client@example.co.za"
                  data-testid="send-draft-recipients"
                />
                <p className="text-[11px] text-slate-400">Pre-filled from the customer record. Comma- or space-separated.</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Cover note (optional)</Label>
                <Textarea
                  rows={3}
                  value={sendDialog.note}
                  onChange={(e) => setSendDialog((s) => ({ ...s, note: e.target.value }))}
                  placeholder="Goes in the email body, above the attached PDF."
                  data-testid="send-draft-note"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialog(null)}>Cancel</Button>
            <Button
              onClick={handleSend}
              disabled={sendBusy}
              className="bg-[#80C030] hover:bg-[#6FA528] text-white font-semibold gap-1"
              data-testid="confirm-send-draft-btn"
            >
              {sendBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sendDialog?.item?.report_sent_at ? 'Resend' : 'Mark Reviewed & Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


const MiniKpi = ({ label, value, accent }) => (
  <Card className="bg-white border-slate-200">
    <CardContent className="p-4">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-2xl font-bold font-heading mt-1 ${accent ? 'text-[#80C030]' : 'text-[#0D5B69]'}`}>{value}</p>
    </CardContent>
  </Card>
);




const PanelLoading = () => (
  <div className="flex items-center justify-center py-20 text-slate-400 gap-2">
    <Loader2 className="w-5 h-5 animate-spin" /> Loading report data…
  </div>
);



// ---------------- DRILL-DOWN DIALOG ----------------
// Reused by Customers + Contractors panels. Opens a side dialog with every incident
// that contributed to the row's totals: incident #, date, service, contractor(s),
// minutes, rate, client amount, contractor amount, profit, status.

const DrillDownDialog = ({ drill, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!drill) { setData(null); return; }
    setLoading(true);
    const url = drill.kind === 'customer'
      ? `/billing/customer/${drill.id}/statement`
      : `/billing/contractor/${drill.id}/statement`;
    api.get(url, { params: { start: drill.start, end: drill.end } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [drill]);

  const downloadPdf = async () => {
    if (!drill) return;
    const url = drill.kind === 'customer'
      ? `/billing/customer/${drill.id}/statement/pdf`
      : `/billing/contractor/${drill.id}/statement/pdf`;
    try {
      const r = await api.get(url, { params: { start: drill.start, end: drill.end }, responseType: 'blob' });
      const u = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = u;
      a.download = `${drill.kind}-${drill.name.replace(/\W+/g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error('PDF export failed'); }
  };

  const _writeCsv = (name, header, rows) => {
    const csv = '\ufeff' + [header, ...rows].map((r) => r.map((c) => {
      const v = c == null ? '' : String(c);
      return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    }).join(',')).join('\n');
    const u = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = u;
    a.download = `${name.replace(/\W+/g, '_')}.csv`;
    a.click();
    window.URL.revokeObjectURL(u);
  };

  const downloadCsv = () => {
    const isContractor = drill?.kind === 'contractor';
    if (isContractor) {
      const rows = (data?.rows || []).map((r) => [
        r.incident_no,
        (r.started_at || '').slice(0, 10),
        r.customer_name,
        r.service_name,
        r.duration_minutes,
        r.rate,
        r.amount,
      ]);
      const header = ['Incident #', 'Date', 'Customer', 'Service', 'Minutes', 'Rate', 'Amount'];
      _writeCsv(`contractor-${drill.name}`, header, rows);
      return;
    }
    const summaries = data?.summaries || [];
    const header = ['Incident #', 'Date', 'Service(s)', 'Contractor(s)', 'Minutes', 'Subtotal', 'VAT', 'Total (incl. VAT)', 'Status'];
    const rows = summaries.map((s) => {
      const services = [...new Set((s.segments || []).map((g) => g.service_name))].join('; ');
      const cons = [...new Set((s.segments || []).map((g) => g.contractor_name))].join('; ');
      const mins = (s.segments || []).reduce((a, g) => a + (g.duration_minutes || 0), 0);
      return [
        s.incident_no,
        (s.opened_at || '').slice(0, 10),
        services || '—',
        cons || '—',
        mins,
        s.client_subtotal,
        s.client_vat,
        s.client_total,
        s.no_bill ? `No Bill — ${s.no_bill_reason || ''}` : 'Billable',
      ];
    });
    _writeCsv(`customer-${drill.name}`, header, rows);
  };

  const isCustomer = drill?.kind === 'customer';
  const isContractor = drill?.kind === 'contractor';
  // Customer statement returns { summaries: [...] } where each summary is an incident with segments.
  // Contractor statement returns { rows: [...] } where each row is ONE assignment-segment for this contractor.
  const customerSummaries = data?.summaries || [];
  const contractorRows = data?.rows || [];
  const flat = isContractor ? contractorRows : customerSummaries;

  const totalSubtotal = customerSummaries.reduce((a, s) => a + (s.client_subtotal || 0), 0);
  const totalVat = customerSummaries.reduce((a, s) => a + (s.client_vat || 0), 0);
  const totalTotal = customerSummaries.reduce((a, s) => a + (s.client_total || 0), 0);
  const contractorTotal = isContractor
    ? (data?.total || 0)
    : customerSummaries.reduce((a, s) => a + (s.contractor_total || 0), 0);

  return (
    <Dialog open={!!drill} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="drilldown-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle className="text-[#0D5B69] flex items-center gap-2">
                {isCustomer && <Users className="w-5 h-5" />}
                {isContractor && <ShieldCheck className="w-5 h-5" />}
                {drill?.name}
              </DialogTitle>
              <DialogDescription>
                {data?.period_label || `Period: ${drill?.start} → ${drill?.end}`} ·
                {' '}{isContractor ? 'Per-segment billing detail.' : 'Per-incident billing detail.'}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" size="sm" className="gap-1" onClick={downloadCsv} data-testid="drilldown-csv-btn">
                <FileSpreadsheet className="w-4 h-4" /> CSV
              </Button>
              <Button variant="outline" size="sm" className="gap-1" onClick={downloadPdf} data-testid="drilldown-pdf-btn">
                <FileText className="w-4 h-4" /> PDF
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pb-3 border-b border-slate-100">
          <MiniStat label={isContractor ? 'Segments' : 'Incidents'} value={fmtInt(flat.length)} />
          <MiniStat label="Client Subtotal" value={fmtR(totalSubtotal)} />
          <MiniStat label="Client Total (incl. VAT)" value={fmtR(totalTotal)} accent />
          <MiniStat label={isContractor ? 'Contractor Earnings' : 'Contractor Cost'} value={fmtR(contractorTotal)} />
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading ? <PanelLoading /> : flat.length === 0 ? (
            <p className="text-center text-slate-400 py-12">No incidents in this period.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                {isCustomer ? (
                  <tr>
                    {['Incident #', 'Date', 'Service(s)', 'Contractor(s)', 'Mins', 'Subtotal', 'VAT', 'Total', 'Status'].map((h) => (
                      <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider p-2.5">{h}</th>
                    ))}
                  </tr>
                ) : (
                  <tr>
                    {['Incident #', 'Date', 'Customer', 'Service', 'Mins', 'Rate', 'Contractor Amt', 'Client Amt', 'Status'].map((h) => (
                      <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider p-2.5">{h}</th>
                    ))}
                  </tr>
                )}
              </thead>
              <tbody>
                {isCustomer ? flat.map((s, idx) => {
                  const services = [...new Set((s.segments || []).map((g) => g.service_name))].join(', ');
                  const cons = [...new Set((s.segments || []).map((g) => g.contractor_name))].join(', ');
                  const mins = (s.segments || []).reduce((a, g) => a + (g.duration_minutes || 0), 0);
                  return (
                    <tr key={s.incident_id} className="border-b border-slate-100 hover:bg-slate-50/80" data-testid={`drilldown-row-${idx}`}>
                      <td className="p-2.5 font-mono text-xs font-semibold text-[#0D5B69]">{s.incident_no}</td>
                      <td className="p-2.5 text-xs text-slate-600">{(s.opened_at || '').slice(0, 10)}</td>
                      <td className="p-2.5 text-xs text-slate-700">{services || '—'}</td>
                      <td className="p-2.5 text-xs text-slate-700">{cons || '—'}</td>
                      <td className="p-2.5 text-xs text-slate-700">{mins}</td>
                      <td className="p-2.5 font-mono text-xs text-slate-700">{fmtR(s.client_subtotal)}</td>
                      <td className="p-2.5 font-mono text-xs text-slate-700">{fmtR(s.client_vat)}</td>
                      <td className="p-2.5 font-mono text-xs font-semibold text-slate-900">{fmtR(s.client_total)}</td>
                      <td className="p-2.5">
                        {s.no_bill
                          ? <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]" title={s.no_bill_reason || ''}>No Bill</Badge>
                          : <Badge className="bg-[#80C030]/10 text-[#5a8c20] border-[#80C030]/30 text-[10px]">Billable</Badge>}
                      </td>
                    </tr>
                  );
                }) : flat.map((row, idx) => (
                  <tr key={`${row.incident_id}-${idx}`} className="border-b border-slate-100 hover:bg-slate-50/80" data-testid={`drilldown-row-${idx}`}>
                    <td className="p-2.5 font-mono text-xs font-semibold text-[#0D5B69]">{row.incident_no}</td>
                    <td className="p-2.5 text-xs text-slate-600">{(row.started_at || '').slice(0, 10)}</td>
                    <td className="p-2.5 text-xs text-slate-700">{row.customer_name}</td>
                    <td className="p-2.5 text-xs text-slate-700">{row.service_name}</td>
                    <td className="p-2.5 text-xs text-slate-700">{row.duration_minutes}</td>
                    <td className="p-2.5 font-mono text-xs text-slate-700">{fmtR(row.rate)}/h</td>
                    <td className="p-2.5 font-mono text-xs font-semibold text-slate-900">{fmtR(row.amount)}</td>
                    <td className="p-2.5 font-mono text-xs text-slate-700">—</td>
                    <td className="p-2.5">
                      <Badge className="bg-[#80C030]/10 text-[#5a8c20] border-[#80C030]/30 text-[10px]">Billable</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};


const MiniStat = ({ label, value, accent }) => (
  <div className="bg-slate-50 rounded-md p-2.5 border border-slate-100">
    <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className={`text-lg font-bold font-heading ${accent ? 'text-[#80C030]' : 'text-[#0D5B69]'}`}>{value}</p>
  </div>
);



// ---------------- ALL INCIDENTS (search + export) ----------------

const AllIncidentsPanel = ({ start, end }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [serviceId, setServiceId] = useState('all');
  const [customerId, setCustomerId] = useState('all');
  const [contractorId, setContractorId] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [openIncident, setOpenIncident] = useState(null);

  useEffect(() => {
    Promise.all([api.get('/services'), api.get('/customers'), api.get('/contractors')])
      .then(([s, c, co]) => { setServices(s.data || []); setCustomers(c.data || []); setContractors(co.data || []); })
      .catch(() => { /* ignored */ });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start, end };
      if (serviceId !== 'all') params.service_id = serviceId;
      if (customerId !== 'all') params.customer_id = customerId;
      if (contractorId !== 'all') params.contractor_id = contractorId;
      if (statusFilter !== 'all') params.status = statusFilter;
      const r = await api.get('/reports/incidents-list', { params });
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [start, end, serviceId, customerId, contractorId, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useLiveEvents(
    ['incident.created', 'incident.updated', 'incident.closed',
      'incident.cancelled', 'incident.reopened',
      'incident.assignment_added', 'incident.assignment_ended'],
    () => { load(); },
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.incident_no?.toLowerCase().includes(q)
      || r.customer_name?.toLowerCase().includes(q)
      || r.service_name?.toLowerCase().includes(q)
      || r.location_address?.toLowerCase().includes(q)
      || r.po_number?.toLowerCase().includes(q)
      || r.contractors?.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const totals = useMemo(() => ({
    count: filtered.length,
    closed: filtered.filter((r) => r.status === 'closed').length,
    open: filtered.filter((r) => r.status === 'open' || r.status === 'in_progress').length,
    cancelled: filtered.filter((r) => r.status === 'cancelled').length,
    revenue: filtered.reduce((a, r) => a + (r.client_total || 0), 0),
    minutes: filtered.reduce((a, r) => a + (r.duration_minutes || 0), 0),
  }), [filtered]);

  const exportAs = async (fmt) => {
    try {
      const params = { fmt, start, end };
      if (serviceId !== 'all') params.service_id = serviceId;
      if (customerId !== 'all') params.customer_id = customerId;
      if (contractorId !== 'all') params.contractor_id = contractorId;
      if (statusFilter !== 'all') params.status = statusFilter;
      const r = await api.get('/reports/incidents_list/export', { params, responseType: 'blob' });
      const u = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = u; a.download = `incidents-list.${fmt}`; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error('Export failed'); }
  };

  const openPreview = async (row) => {
    try {
      const r = await api.get(`/incidents/${row.id}`);
      setOpenIncident(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="all-incidents-panel">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MiniKpi label="Total" value={fmtInt(totals.count)} accent />
        <MiniKpi label="Open / In Progress" value={fmtInt(totals.open)} />
        <MiniKpi label="Closed" value={fmtInt(totals.closed)} />
        <MiniKpi label="Cancelled" value={fmtInt(totals.cancelled)} />
        <MiniKpi label="Revenue (incl VAT)" value={fmtR(totals.revenue)} />
      </div>

      <Card className="bg-white border-slate-200" data-testid="all-incidents-card">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4">
          <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-3">
            <div>
              <CardTitle className="font-heading text-lg text-[#0D5B69] flex items-center gap-2">
                <FileText className="w-5 h-5" /> All Incidents
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Filter by date range above, plus service / customer / contractor / status below.
                Click a row to drill down — export the filtered set as CSV, XLSX or PDF.
              </p>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={() => exportAs('csv')} data-testid="export-incidents-csv">
                <FileSpreadsheet className="w-3.5 h-3.5" /> CSV
              </Button>
              <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={() => exportAs('xlsx')} data-testid="export-incidents-xlsx">
                <FileSpreadsheet className="w-3.5 h-3.5" /> XLSX
              </Button>
              <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={() => exportAs('pdf')} data-testid="export-incidents-pdf">
                <FileText className="w-3.5 h-3.5" /> PDF
              </Button>
            </div>
          </div>

          {/* Filter row */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2 mt-3">
            <Input
              placeholder="Search INC #, customer, location, PO…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="incidents-search"
              className="h-9"
            />
            <Select value={serviceId} onValueChange={setServiceId}>
              <SelectTrigger className="h-9" data-testid="incidents-service-filter"><SelectValue placeholder="Service" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger className="h-9" data-testid="incidents-customer-filter"><SelectValue placeholder="Customer" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All Customers</SelectItem>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={contractorId} onValueChange={setContractorId}>
              <SelectTrigger className="h-9" data-testid="incidents-contractor-filter"><SelectValue placeholder="Contractor" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All Contractors</SelectItem>
                {contractors.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9" data-testid="incidents-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? <PanelLoading /> : filtered.length === 0 ? (
            <p className="text-center text-slate-400 py-10">No incidents match the current filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {['Incident #', 'Date', 'Customer', 'Reg Plate', 'Service', 'Contractors', 'Status', 'Duration', 'Total', ''].map((h) => (
                      <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider p-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, idx) => (
                    <tr
                      key={r.id}
                      className="border-b border-slate-100 hover:bg-[#80C030]/5 cursor-pointer"
                      onClick={() => openPreview(r)}
                      data-testid={`incident-row-${idx}`}
                      title="Click to drill down"
                    >
                      <td className="p-3 font-mono text-xs font-semibold text-[#0D5B69]">{r.incident_no}</td>
                      <td className="p-3 text-xs text-slate-600">{(r.opened_at || '').slice(0, 10)}</td>
                      <td className="p-3 text-xs text-slate-700">{r.customer_name}</td>
                      <td className="p-3 font-mono text-[11px] text-slate-700 uppercase tracking-wider">{r.vehicle_registration || '—'}</td>
                      <td className="p-3 text-xs text-slate-700">{r.service_name}</td>
                      <td className="p-3 text-xs text-slate-700 truncate max-w-[200px]">{r.contractors}</td>
                      <td className="p-3"><StatusBadge status={r.status} /></td>
                      <td className="p-3 text-xs text-slate-700">{r.duration_minutes} min</td>
                      <td className="p-3 font-mono text-xs font-semibold text-slate-900">
                        {r.no_bill ? <span className="text-slate-400 italic">No Bill</span> : fmtR(r.client_total)}
                      </td>
                      <td className="p-3 text-right"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {openIncident && (
        <IncidentDialog
          open
          onOpenChange={(o) => !o && setOpenIncident(null)}
          incident={openIncident}
          customers={customers}
          services={services}
          contractors={contractors}
          onSaved={(updated) => { setOpenIncident(updated); load(); }}
        />
      )}
    </div>
  );
};


const ProfitabilityPanel = ({ start, end }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [customerId, setCustomerId] = useState('all');
  const [contractorId, setContractorId] = useState('all');
  const [customers, setCustomers] = useState([]);
  const [contractors, setContractors] = useState([]);

  useEffect(() => {
    Promise.all([api.get('/customers'), api.get('/contractors')])
      .then(([c, co]) => { setCustomers(c.data || []); setContractors(co.data || []); })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start, end };
      if (customerId !== 'all') params.customer_id = customerId;
      if (contractorId !== 'all') params.contractor_id = contractorId;
      params.status = 'closed';   // profit only makes sense on completed cases
      const r = await api.get('/reports/incidents-list', { params });
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [start, end, customerId, contractorId]);
  useEffect(() => { load(); }, [load]);

  useLiveEvents(
    ['incident.closed', 'incident.assignment_ended'],
    () => load(),
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.incident_no?.toLowerCase().includes(q)
      || r.customer_name?.toLowerCase().includes(q)
      || r.contractors?.toLowerCase().includes(q)
      || r.vehicle_registration?.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const totals = useMemo(() => {
    const cost = filtered.reduce((a, r) => a + (r.contractor_total || 0), 0);
    const exp = filtered.reduce((a, r) => a + (r.expenses_total || 0), 0);
    const bill = filtered.reduce((a, r) => a + (r.client_subtotal || 0), 0);
    const profit = filtered.reduce((a, r) => a + (r.profit || 0), 0);
    const margin = bill > 0 ? (profit / bill) * 100 : 0;
    return { cost, exp, bill, profit, margin, count: filtered.length };
  }, [filtered]);

  const exportAs = async (fmt) => {
    try {
      const params = { fmt, start, end, status: 'closed' };
      if (customerId !== 'all') params.customer_id = customerId;
      if (contractorId !== 'all') params.contractor_id = contractorId;
      const r = await api.get('/reports/profitability/export', { params, responseType: 'blob' });
      const u = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = u; a.download = `profitability.${fmt}`; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error('Export failed'); }
  };

  return (
    <Card className="bg-white border-slate-200" data-testid="profitability-card">
      <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4">
        <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-3">
          <div>
            <CardTitle className="font-heading text-lg text-[#0D5B69] flex items-center gap-2">
              <TrendingUp className="w-5 h-5" /> Profitability Report
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Closed incidents only · Gross Profit = Client Bill (excl VAT) − Contractor Cost.
              Filter by date range above, plus customer / contractor below.
            </p>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={() => exportAs('csv')} data-testid="export-profit-csv">
              <FileSpreadsheet className="w-3.5 h-3.5" /> CSV
            </Button>
            <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={() => exportAs('xlsx')} data-testid="export-profit-xlsx">
              <FileSpreadsheet className="w-3.5 h-3.5" /> XLSX
            </Button>
            <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={() => exportAs('pdf')} data-testid="export-profit-pdf">
              <FileText className="w-3.5 h-3.5" /> PDF
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
          <Input
            placeholder="Search INC#, customer, contractor, reg plate…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="profit-search" className="h-9"
          />
          <Select value={customerId} onValueChange={setCustomerId}>
            <SelectTrigger className="h-9" data-testid="profit-customer-filter"><SelectValue placeholder="Customer" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All Customers</SelectItem>
              {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={contractorId} onValueChange={setContractorId}>
            <SelectTrigger className="h-9" data-testid="profit-contractor-filter"><SelectValue placeholder="Contractor" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All Contractors</SelectItem>
              {contractors.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {/* Totals strip */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-3">
          <MiniKpi label="Closed Incidents" value={fmtInt(totals.count)} />
          <MiniKpi label="Contractor Cost" value={fmtR(totals.cost)} />
          <MiniKpi label="S&T / Expenses" value={fmtR(totals.exp)} />
          <MiniKpi label="Client Bill (ex VAT)" value={fmtR(totals.bill)} />
          <MiniKpi label="Gross Profit" value={fmtR(totals.profit)} accent={totals.profit >= 0} />
          <MiniKpi label="Blended Margin" value={`${totals.margin.toFixed(1)}%`} accent={totals.margin >= 0} />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? <PanelLoading /> : filtered.length === 0 ? (
          <p className="text-center text-slate-400 py-10">No closed incidents in this window.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {['Incident #', 'Date', 'Customer', 'Reg Plate', 'Service', 'Contractors',
                    'Contractor Cost', 'S&T / Expenses', 'Client Bill', 'Gross Profit', 'Margin %'].map((h) => (
                    <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider p-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, idx) => {
                  const margin = r.client_subtotal > 0 ? (r.profit / r.client_subtotal) * 100 : 0;
                  return (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-[#80C030]/5" data-testid={`profit-row-${idx}`}>
                      <td className="p-3 font-mono text-xs font-semibold text-[#0D5B69]">{r.incident_no}</td>
                      <td className="p-3 text-xs text-slate-600">{(r.opened_at || '').slice(0, 10)}</td>
                      <td className="p-3 text-xs text-slate-700">{r.customer_name}</td>
                      <td className="p-3 font-mono text-[11px] text-slate-700 uppercase tracking-wider">{r.vehicle_registration || '—'}</td>
                      <td className="p-3 text-xs text-slate-700">{r.service_name}</td>
                      <td className="p-3 text-xs text-slate-700 truncate max-w-[180px]">{r.contractors}</td>
                      <td className="p-3 font-mono text-xs text-slate-700">{fmtR(r.contractor_total || 0)}</td>
                      <td className="p-3 font-mono text-xs text-slate-700" data-testid={`profit-expenses-${idx}`}>{fmtR(r.expenses_total || 0)}</td>
                      <td className="p-3 font-mono text-xs text-slate-700">{fmtR(r.client_subtotal || 0)}</td>
                      <td className={`p-3 font-mono text-xs font-semibold ${(r.profit || 0) < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{fmtR(r.profit || 0)}</td>
                      <td className={`p-3 font-mono text-xs ${margin < 0 ? 'text-red-600' : 'text-slate-700'}`}>{margin.toFixed(1)}%</td>
                    </tr>
                  );
                })}
                <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                  <td className="p-3 text-xs text-slate-900" colSpan={6}>TOTAL</td>
                  <td className="p-3 font-mono text-xs text-slate-900">{fmtR(totals.cost)}</td>
                  <td className="p-3 font-mono text-xs text-slate-900">{fmtR(totals.exp)}</td>
                  <td className="p-3 font-mono text-xs text-slate-900">{fmtR(totals.bill)}</td>
                  <td className={`p-3 font-mono text-xs ${totals.profit < 0 ? 'text-red-600' : 'text-emerald-800'}`}>{fmtR(totals.profit)}</td>
                  <td className={`p-3 font-mono text-xs ${totals.margin < 0 ? 'text-red-600' : 'text-slate-900'}`}>{totals.margin.toFixed(1)}%</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


const StatusBadge = ({ status }) => {
  const map = {
    open:        { cls: 'bg-red-100 text-red-700 border-red-200', label: 'Open' },
    in_progress: { cls: 'bg-orange-100 text-orange-700 border-orange-200', label: 'In Progress' },
    closed:      { cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Closed' },
    cancelled:   { cls: 'bg-slate-100 text-slate-600 border-slate-200', label: 'Cancelled' },
  };
  const b = map[status] || { cls: '', label: status || '—' };
  return <Badge className={`${b.cls} text-[10px]`}>{b.label}</Badge>;
};
